import { Component, Element, Fragment, h, Host, Prop } from '@stencil/core';
import { ComponentView, Price, TotalPriceCalculation } from '../../../types/types';
import { FormRenderProps, FormValues } from '../../stencil-form/declarations';
import { Cart, OrderKind, PaymentMethod, RtrError, ShippingItem } from '../../../redux/types';
import { PaymentStepState, renderTerm } from './utils';
import { t } from '../../../redux/utils';
import { PaymentCustomer } from '../../rtr-payment/utils/types';
import { AccordionNode } from '../../atoms/rtr-accordion/utils';
import { StaticCartArticle } from '../../rentware-cart/rentware-cart-article/interfaces';
import { makeStaticArticles } from '../../rentware-cart/utils';
import { HostElement } from '@stencil/core/internal';
import { ButtonSize } from '../../atoms/rentware-button/types';

@Component({
  tag: 'rtr-step-payment-template',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RtrStepPaymentTemplate {
  @Prop() orderReference: string;
  @Prop() customerMessage;
  @Prop() isRecurringMode: boolean;
  @Prop() currentMethod: PaymentMethod;
  @Prop() secondaryMethod: PaymentMethod;
  @Prop() orderKind: OrderKind;
  @Prop() saveCustomerNotes;
  @Prop() terms;
  @Prop() termsFormRef;
  @Prop() termsRefs;
  @Prop() system;
  @Prop() currentStep;
  @Prop() errors: RtrError[];
  @Prop() showVoucher: boolean;
  @Prop() totalPrice: Price;
  @Prop() depositPrice: Price;
  @Prop() priceWithSplit: Price;
  @Prop() cart: Cart;
  @Prop() customer: any;
  @Prop() billingStepValues: any;
  @Prop() calculatedAmountToPay: number;
  @Prop() currency: string;
  @Prop() currencyPosition: string;
  @Prop() shipping?: ShippingItem;
  @Prop() stepState: PaymentStepState;
  @Prop() onVoucherSuccess: () => void;
  @Prop() setCheckoutWaiting: (value: boolean) => void;
  @Prop() onActionButton: () => void;
  @Prop() onFormChange: () => void;
  @Prop() setMethodValidated: (value: boolean) => void;

  @Element() el;
  containerEl: HostElement;

  get customerNoteTitleText(): string {
    return t('payment.customerNotesTitle');
  }

  get customerNoteLabelText(): string {
    return t('payment.customerNotesLabel');
  }

  get customerNotePlaceholderText(): string {
    return t('payment.customerNotesPlaceholder');
  }

  get voucherTitleText(): string {
    return t('payment.voucherTitle');
  }

  get payNowText() {
    return t('cart.payAmount');
  }

  get totalText() {
    return t('cart.total');
  }

  get actionText(): string {
    if (this.stepState === PaymentStepState.SelectPayment) {
      return t('checkout.chooseMethod');
    } else if (this.stepState === PaymentStepState.ProvideMethod) {
      return t('checkout.fillPayment');
    } else if (this.stepState === PaymentStepState.AcceptTerms) {
      return t('checkout.acceptTerms');
    }

    let key = this.orderKind === OrderKind.Sale ? 'cart.defaultSaleBtn' : 'cart.defaultBtn';
    if (this.currentMethod?.value === 'paypal' || this.secondaryMethod?.value === 'paypal') {
      key = 'cart.defaultPaypalBtn';
    }

    return t(key);
  }

  get paymentCustomer(): PaymentCustomer {
    return {
      email: this.billingStepValues?.email || this.customer?.customer?.email,
      fullName: this.customer.customer.firstName + ' ' + this.customer.customer.lastName,
      country: this.customer?.address?.country,
    };
  }

  get articlesText() {
    if (this.cart.totalQuantityWithShipping === 1) {
      return '1 ' + t('cart.articleInCart');
    }

    return this.cart.totalQuantityWithShipping + ' ' + t('cart.articlesInCart');
  }

  get articles(): StaticCartArticle[] {
    return makeStaticArticles(this.cart);
  }

  get cartNodes(): AccordionNode[] {
    const allowTotalPrice = this.hasTotalPrice()
    const shipping = this.cart.shipping ? {...this.cart.shipping, totalPrice: this.cart.shipping.price} : null

    return [
      {
        expanded: <rentware-cart-template-timer />,
        collapsed: <rentware-expiration-timer component={ComponentView.Checkout} />,
        hideCollapsedOnExpanded: true,
      },
      {
        expanded: <rtr-step-payment-articles articles={this.articles} shipping={shipping} allowTotalPrice={allowTotalPrice} />,
        collapsed: <span class="RTCDAccordionRow">{this.articlesText}</span>,
        hideCollapsedOnExpanded: true,
      },
    ];
  }

  hasMonthlyPrice(): boolean {
    return this.cart?.totalPriceCalculation === TotalPriceCalculation.Month
  }

  hasTotalPrice(): boolean {
    return this.cart?.totalPriceCalculation === TotalPriceCalculation.Interval || !this.isRecurringMode
  }

  hasDepositPrice(): boolean {
    return this.depositPrice?.gross > 0
  }

  render() {
    return (
      <Host class="RCPContainer" ref={el => (this.containerEl = el)}>
        <div class="RCPScrollButton"></div>

        <div class="RCPActions">
          <rentware-spacer size="M" />

          <rtr-payment
            orderReference={this.orderReference}
            amountToPay={this.calculatedAmountToPay}
            paymentCustomer={this.paymentCustomer}
            depositPrice={this.cart.depositPrice}
            setParentWaiting={this.setCheckoutWaiting}
            setMethodValidated={this.setMethodValidated}
          />

          <rentware-spacer size="L" />

          <div class="PaymentOptionsContainer">
            <rentware-spacer size="S" />

            {this.showVoucher && (
              <Fragment>
                <div class="RCPPaymentTitle">{this.voucherTitleText}</div>

                <rentware-spacer size="S" />

                <rtr-cart-voucher onVoucherSuccess={this.onVoucherSuccess} />

                <rentware-spacer size="L" />
              </Fragment>
            )}

            <div class="RCPPaymentTitle">{this.customerNoteTitleText}</div>
            <rentware-spacer size="S" />

            <div class="TextAreaWrapper">
              <label class="RCPTextAreaLabel">{this.customerNoteLabelText}</label>

              <textarea
                placeholder={this.customerNotePlaceholderText}
                value={this.customerMessage}
                onInput={(event: Event) => (this.customerMessage = (event.target as any).value)}
                onBlur={() => this.saveCustomerNotes(this.customerMessage, undefined, undefined)}
              />
            </div>

            <rentware-spacer size="L" />
          </div>
        </div>
        <div class="RCPSummary">
          <div class="RCPAccordion">
            <rtr-accordion startingIndex={1} nodes={this.cartNodes} view={ComponentView.Checkout} />
          </div>

          <div class="RCPTerms">

            {this.hasMonthlyPrice() &&
              <div class="RCPPriceRow">
                <div class="RCPPriceFieldR">
                  <div>
                    <div class="RCPPriceFieldL">
                      <rentware-total-price price={this.cart?.discountedPricePerMonthWithoutDeposit} />
                    </div>
                  </div>
                </div>
              </div>
            }

            {this.hasTotalPrice() &&
            <div class="RCPPriceRow">
              <div class="RCPPriceFieldR GlobalBold">
                <span class="GlobalEllipsis">{this.totalText}</span>
                <rentware-total-price price={this.totalPrice} showTotalText={false} />
              </div>
              <div class="RCPPriceFieldL">
                <span style={{ fontSize: '13px' }}>
                  <rentware-total-vat price={this.totalPrice} />
                </span>
              </div>
            </div>
            }

            {this.hasDepositPrice() &&
            <div class="RCPPriceRow">
              <div class="RCPPriceFieldR">
                <div>
                    <div class="RCPPriceFieldL">
                      <rentware-deposit-price price={this.depositPrice} />
                    </div>
                </div>
              </div>
            </div>
            }

            <rentware-spacer size="S" />

            <div class="RCPPriceRow">
              <div class="RCPPriceFieldR GlobalBold">
                <span class="GlobalEllipsis" title={this.payNowText}>
                  {this.payNowText}
                </span>

                <span class="RCPPriceValue">
                  <rentware-total-price price={this.priceWithSplit} showTotalText={false} />
                </span>
              </div>

            </div>

            <rentware-spacer size="S" />

            <div class="RCPTermsScrollable">
              <stencil-form
                ref={el => {
                  this.termsFormRef(el);
                }}
                initialValues={{}}
                onFormSubmit={e => {
                  e.preventDefault();
                }}
                onInput={this.onFormChange}
                validateOnInput={false}
                renderer={(props: FormRenderProps<FormValues>) => {
                  return this.terms.map(item => {
                    return (
                      <div class="RCPTermsItem">
                        {renderTerm(
                          item, //
                          this.system.company,
                          this.currentMethod?.value,
                          props,
                          el => this.termsRefs(el),
                          true,
                        )}
                      </div>
                    );
                  });
                }}
              />
            </div>

            <div class="RCPTermsButton">
              <rentware-button
                appearance={{ fullWidth: true, size: ButtonSize.XL }}
                buttonText={this.actionText}
                onClick={this.onActionButton}
              />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
